import { createShardedChunkGetter } from "./codecs/sharding.js";
import { createCodecPipeline } from "./codecs.js";
import { assertSharedArrayBufferAvailable, createBuffer, createChunkKeyEncoder, ensureCorrectScalar, getCtr, getStrides, isDataType, isShardingCodec, } from "./util.js";
export class Location {
    store;
    path;
    constructor(store, path = "/") {
        this.store = store;
        this.path = path;
    }
    resolve(path) {
        // reuse URL resolution logic built into the browser
        // handles relative paths, absolute paths, etc.
        let root = new URL(`file://${this.path.endsWith("/") ? this.path : `${this.path}/`}`);
        return new Location(this.store, decodeURIComponent(new URL(path, root).pathname));
    }
}
export function root(store) {
    return new Location(store ?? new Map());
}
export class Group extends Location {
    kind = "group";
    #metadata;
    constructor(store, path, metadata) {
        super(store, path);
        this.#metadata = metadata;
    }
    get attrs() {
        return this.#metadata.attributes;
    }
}
function getArrayOrder(codecs) {
    const maybeTransposeCodec = codecs.find((c) => c.name === "transpose");
    // @ts-expect-error - TODO: Should validate?
    return maybeTransposeCodec?.configuration?.order ?? "C";
}
const CONTEXT_MARKER = Symbol("zarrita.context");
export function getContext(obj) {
    return obj[CONTEXT_MARKER];
}
function createContext(location, metadata) {
    let { configuration } = metadata.codecs.find(isShardingCodec) ?? {};
    let sharedContext = {
        encodeChunkKey: createChunkKeyEncoder(metadata.chunk_key_encoding),
        TypedArray: getCtr(metadata.data_type),
        fillValue: metadata.fill_value,
    };
    if (configuration) {
        let nativeOrder = getArrayOrder(configuration.codecs);
        return {
            ...sharedContext,
            kind: "sharded",
            chunkShape: configuration.chunk_shape,
            codec: createCodecPipeline({
                dataType: metadata.data_type,
                shape: configuration.chunk_shape,
                codecs: configuration.codecs,
                fillValue: metadata.fill_value,
            }),
            getStrides(shape) {
                return getStrides(shape, nativeOrder);
            },
            getChunkBytes: createShardedChunkGetter(location, metadata.chunk_grid.configuration.chunk_shape, sharedContext.encodeChunkKey, configuration),
        };
    }
    let nativeOrder = getArrayOrder(metadata.codecs);
    return {
        ...sharedContext,
        kind: "regular",
        chunkShape: metadata.chunk_grid.configuration.chunk_shape,
        codec: createCodecPipeline({
            dataType: metadata.data_type,
            shape: metadata.chunk_grid.configuration.chunk_shape,
            codecs: metadata.codecs,
            fillValue: metadata.fill_value,
        }),
        getStrides(shape) {
            return getStrides(shape, nativeOrder);
        },
        async getChunkBytes(chunkCoords, options) {
            let chunkKey = sharedContext.encodeChunkKey(chunkCoords);
            let chunkPath = location.resolve(chunkKey).path;
            return location.store.get(chunkPath, options);
        },
    };
}
export class Array extends Location {
    kind = "array";
    #metadata;
    [CONTEXT_MARKER];
    constructor(store, path, metadata) {
        super(store, path);
        this.#metadata = {
            ...metadata,
            fill_value: ensureCorrectScalar(metadata),
        };
        this[CONTEXT_MARKER] = createContext(this, this.#metadata);
    }
    get attrs() {
        return this.#metadata.attributes;
    }
    get dimensionNames() {
        return this.#metadata.dimension_names;
    }
    get fillValue() {
        return this.#metadata.fill_value;
    }
    get shape() {
        return this.#metadata.shape;
    }
    get chunks() {
        return this[CONTEXT_MARKER].chunkShape;
    }
    get dtype() {
        return this.#metadata.data_type;
    }
    async getChunk(chunkCoords, options, opts) {
        if (opts?.useSharedArrayBuffer) {
            assertSharedArrayBufferAvailable();
        }
        let context = this[CONTEXT_MARKER];
        let maybeBytes = await context.getChunkBytes(chunkCoords, options);
        if (!maybeBytes) {
            let size = context.chunkShape.reduce((a, b) => a * b, 1);
            let data;
            if (opts?.useSharedArrayBuffer) {
                let sample = new context.TypedArray(0);
                if (!("BYTES_PER_ELEMENT" in sample)) {
                    console.warn("zarrita: useSharedArrayBuffer is not supported for non-buffer-backed data types.");
                    data = new context.TypedArray(size);
                }
                else {
                    let buffer = createBuffer(size * sample.BYTES_PER_ELEMENT, true);
                    data = new context.TypedArray(buffer, 0, size);
                }
            }
            else {
                data = new context.TypedArray(size);
            }
            // @ts-expect-error: TS can't infer that `fillValue` is union (assumes never) but this is ok
            data.fill(context.fillValue);
            return {
                data,
                shape: context.chunkShape,
                stride: context.getStrides(context.chunkShape),
            };
        }
        return context.codec.decode(maybeBytes);
    }
    /**
     * A helper method to narrow `zarr.Array` Dtype.
     *
     * ```typescript
     * let arr: zarr.Array<DataType, FetchStore> = zarr.open(store, { kind: "array" });
     *
     * // Option 1: narrow by scalar type (e.g. "bool", "raw", "bigint", "number")
     * if (arr.is("bigint")) {
     *   // zarr.Array<"int64" | "uint64", FetchStore>
     * }
     *
     * // Option 3: exact match
     * if (arr.is("float32")) {
     *   // zarr.Array<"float32", FetchStore, "/">
     * }
     * ```
     */
    is(query) {
        return isDataType(this.dtype, query);
    }
}
//# sourceMappingURL=hierarchy.js.map